import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { ToolCard, Loader, Overline, Breadcrumbs } from "@/components/shared";
import { AdSlot } from "@/components/Ads";
import { Search } from "lucide-react";

const FILTERS = ["All", "Popular", "New", "Free", "Freemium", "Paid"];
const SORTS = [["popular", "Popular"], ["rating", "Rating"], ["newest", "Newest"], ["price", "Price"]];

export default function Tools() {
  const [cats, setCats] = useState([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState("All");
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("popular");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  useSEO({ title: "100+ AI Tools Directory (2026)", description: "Browse 100+ AI tools with official logos, ratings, pricing and reviews. Filter by category, price and popularity." });

  useEffect(() => { api.toolCategories().then((d) => { setCats(d.categories); setTotal(d.total); }); }, []);
  useEffect(() => {
    setItems(null);
    const t = setTimeout(() => api.tools({ category, filter, sort, search }).then((d) => setItems(d.items)), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [category, filter, sort, search]);

  return (
    <div className="klyroo-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "AI Tools" }]} />
        <Overline>Directory</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">AI Tools</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-lg">A serious, searchable database of {total}+ AI tools — each with its own logo, rating, pricing and deep review.</p>

        <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-8">
          {/* sidebar */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="relative mb-5">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="tools-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tools…"
                className="w-full bg-klyroo-surface border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white outline-none focus:border-klyroo-cyan/50" />
            </div>
            <Overline className="mb-3">Categories</Overline>
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0">
              <button onClick={() => setCategory("All")} data-testid="cat-all" className={`text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap flex justify-between ${category === "All" ? "bg-klyroo-cyan/10 text-klyroo-cyan" : "text-slate-300 hover:bg-white/5"}`}>
                All Tools <span className="font-mono text-xs opacity-60">{total}</span>
              </button>
              {cats.map((c) => (
                <button key={c.name} onClick={() => setCategory(c.name)} data-testid={`cat-${c.name.replace(/ /g, "-")}`}
                  className={`text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap flex justify-between gap-2 ${category === c.name ? "bg-klyroo-cyan/10 text-klyroo-cyan" : "text-slate-300 hover:bg-white/5"}`}>
                  {c.name.replace("AI ", "")} <span className="font-mono text-xs opacity-60">{c.count}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* main */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>{f}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-slate-500">Sort</span>
                <select data-testid="tools-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                  className="bg-klyroo-surface border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none">
                  {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            {!items ? <Loader /> : items.length === 0 ? (
              <p className="text-slate-500 py-20 text-center font-mono">No tools match your filters.</p>
            ) : (
              <>
                <p className="text-xs font-mono text-slate-500 mb-4">{items.length} tools</p>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="tools-grid">
                  {items.flatMap((t, i) => {
                    const cells = [<ToolCard key={t.slug} t={t} />];
                    if ((i + 1) % 9 === 0 && i !== items.length - 1) {
                      cells.push(<AdSlot key={`feed-ad-${i}`} placement="feed-square" className="h-full" />);
                    }
                    return cells;
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
